
interface CozeMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface CozeResponse {
  message: string;
  success: boolean;
  error?: string;
}

interface CustomVariables {
  company_name: string;
  dept_name: string;
  phone_number: string;
  user_name_cn: string;
  userid: string;
  role: string;
  position: string;
}

// 引入Coze SDK (通过CDN加载)
declare global {
  interface Window {
    CozeWebSDK: any;
  }
}

class CozeApiService {
  private apiKey: string | null = null;
  private botId = '7554655121180262463';
  private client: any = null;
  private sdkLoadAttempts = 0;
  private maxSdkLoadAttempts = 3;

  constructor() {
    // 从环境变量或本地存储获取API密钥
    const savedKey = localStorage.getItem('coze_api_key');
    if (savedKey) {
      this.apiKey = savedKey;
    } else {
      // 设置默认 API 密钥
      this.apiKey = 'sat_DVWes8e4MfCL27Ye2s3zJHwgBo895ofUztmBnMXyr5lcbw0Hwz2lOTY187u8uNVf';
      localStorage.setItem('coze_api_key', this.apiKey);
    }
    
    this.initializeSDK();
  }

  private async initializeSDK() {
    try {
      // 加载Coze SDK
      await this.loadCozeSDK();
      
      if (window.CozeWebSDK && this.apiKey) {
        this.client = new window.CozeWebSDK({
          token: this.apiKey,
          baseURL: 'https://api.coze.cn'
        });
        console.log('✅ Coze SDK 初始化成功');
      }
    } catch (error) {
      console.error('❌ Coze SDK 初始化失败:', error);
      console.log('⚠️ 将使用 HTTP 接口作为备用方案');
    }
  }

  private loadCozeSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      // 检查是否已经加载
      if (window.CozeWebSDK) {
        resolve();
        return;
      }

      // 如果已经尝试过多次，直接使用HTTP接口
      if (this.sdkLoadAttempts >= this.maxSdkLoadAttempts) {
        console.log('⚠️ SDK 加载尝试次数已达上限，使用 HTTP 接口');
        resolve(); // 不抛出错误，让系统使用HTTP接口
        return;
      }

      this.sdkLoadAttempts++;

      // 尝试多个CDN源
      const cdnSources = [
        'https://unpkg.com/@coze/web-sdk@latest/dist/index.umd.js',
        'https://cdn.jsdelivr.net/npm/@coze/web-sdk@latest/dist/index.umd.js',
        'https://fastly.jsdelivr.net/npm/@coze/web-sdk@latest/dist/index.umd.js'
      ];

      const tryLoadFromSource = (sourceIndex: number) => {
        if (sourceIndex >= cdnSources.length) {
          console.log(`⚠️ 所有 CDN 源都加载失败，使用 HTTP 接口作为备用方案`);
          resolve(); // 不抛出错误，让系统使用HTTP接口
          return;
        }

        const script = document.createElement('script');
        script.src = cdnSources[sourceIndex];
        
        const timeout = setTimeout(() => {
          console.log(`⚠️ CDN 源 ${sourceIndex + 1} 加载超时，尝试下一个源`);
          document.head.removeChild(script);
          tryLoadFromSource(sourceIndex + 1);
        }, 10000); // 10秒超时

        script.onload = () => {
          clearTimeout(timeout);
          console.log(`✅ Coze SDK 从源 ${sourceIndex + 1} 加载成功`);
          resolve();
        };

        script.onerror = () => {
          clearTimeout(timeout);
          console.log(`❌ CDN 源 ${sourceIndex + 1} 加载失败，尝试下一个源`);
          document.head.removeChild(script);
          tryLoadFromSource(sourceIndex + 1);
        };
        
        document.head.appendChild(script);
      };

      tryLoadFromSource(0);
    });
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
    localStorage.setItem('coze_api_key', apiKey);
    // 重新初始化SDK
    this.initializeSDK();
  }

  private getUserVariables(): CustomVariables {
    const userProfile = localStorage.getItem('userProfile');
    if (userProfile) {
      const profile = JSON.parse(userProfile);
      return {
        company_name: profile.company_name || '上海冯云信息科技有限公司',
        dept_name: profile.dept_name || '战略管理部',
        phone_number: profile.phone_number || '17706527820',
        user_name_cn: profile.user_name_cn || profile.user_name_en || '冯鹏程',
        userid: profile.userid || 'makey',
        role: profile.role || '战略管理',
        position: profile.position || '总经理'
      };
    }

    // 如果没有用户信息，返回默认值
    return {
      company_name: '上海冯云信息科技有限公司',
      dept_name: '战略管理部',
      phone_number: '17706527820',
      user_name_cn: '冯鹏程',
      userid: 'makey',
      role: '战略管理',
      position: '总经理'
    };
  }

  async sendMessage(message: string, conversationId?: string): Promise<CozeResponse> {
    return this.sendMessageWithBotId(message, this.botId, conversationId);
  }

  async sendMessageWithBotId(message: string, botId: string, conversationId?: string): Promise<CozeResponse> {
    if (!this.apiKey) {
      return {
        message: '',
        success: false,
        error: '请先配置 Coze API 密钥'
      };
    }

    // 直接使用HTTP接口，更稳定可靠
    console.log('🚀 使用 HTTP 接口调用 Coze API');
    return this.sendMessageViaHTTP(message, botId, conversationId);
  }

  // 增强的SDK内容提取方法
  private extractSDKContent(response: any): string {
    if (!response) {
      console.log('❌ 响应为空');
      return '';
    }

    console.log('🔍 开始提取SDK响应内容...');

    // 策略1: 检查 messages 数组中的助手回复
    if (response.messages && Array.isArray(response.messages)) {
      console.log('📝 检查messages数组，长度:', response.messages.length);
      
      for (const message of response.messages) {
        console.log('📄 消息详情:', {
          role: message.role,
          type: message.type,
          content_type: message.content_type,
          hasContent: !!message.content
        });

        if (message.role === 'assistant' && message.type === 'answer' && message.content) {
          const cleanedContent = this.cleanResponseContent(message.content);
          if (cleanedContent) {
            console.log('✅ 从messages数组提取成功');
            return cleanedContent;
          }
        }
      }
    }

    // 策略2: 检查直接的content字段
    if (response.content) {
      console.log('📄 检查直接content字段');
      const cleanedContent = this.cleanResponseContent(response.content);
      if (cleanedContent) {
        console.log('✅ 从直接content字段提取成功');
        return cleanedContent;
      }
    }

    // 策略3: 检查data字段
    if (response.data) {
      console.log('📄 检查data字段');
      const dataContent = this.extractSDKContent(response.data);
      if (dataContent) {
        console.log('✅ 从data字段提取成功');
        return dataContent;
      }
    }

    // 策略4: 检查是否为单个消息对象
    if (response.role === 'assistant' && response.content) {
      console.log('📄 检查单个消息对象');
      const cleanedContent = this.cleanResponseContent(response.content);
      if (cleanedContent) {
        console.log('✅ 从单个消息对象提取成功');
        return cleanedContent;
      }
    }

    // 策略5: 深度遍历所有可能的内容字段
    const deepContent = this.deepExtractContent(response);
    if (deepContent) {
      console.log('✅ 深度提取成功');
      return deepContent;
    }

    console.log('❌ 所有提取策略都失败了');
    return '';
  }

  // 深度提取内容
  private deepExtractContent(obj: any, depth: number = 0): string {
    if (depth > 5) return ''; // 防止无限递归
    
    if (!obj || typeof obj !== 'object') return '';

    // 如果当前对象有content字段且看起来是有效内容
    if (obj.content && typeof obj.content === 'string') {
      const cleaned = this.cleanResponseContent(obj.content);
      if (cleaned && this.isValidContent(cleaned)) {
        return cleaned;
      }
    }

    // 递归检查所有属性
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && typeof obj[key] === 'object') {
        const result = this.deepExtractContent(obj[key], depth + 1);
        if (result) return result;
      }
    }

    return '';
  }

  // 验证内容是否有效
  private isValidContent(content: string): boolean {
    if (!content || content.length < 2) return false;
    
    // 检查是否包含有意义的文字
    if (!/[a-zA-Z\u4e00-\u9fa5]/.test(content)) return false;
    
    // 排除纯系统信息
    const systemKeywords = ['status', 'success', 'error', 'timestamp', 'id', 'type'];
    const lowerContent = content.toLowerCase();
    
    const isSystemOnly = systemKeywords.every(keyword => !lowerContent.includes(keyword));
    return isSystemOnly || content.length > 10;
  }

  // HTTP接口调用作为回退方案
  private async sendMessageViaHTTP(message: string, botId: string, conversationId?: string): Promise<CozeResponse> {
    try {
      const customVariables = this.getUserVariables();

      const requestBody = {
        bot_id: botId,
        user_id: customVariables.userid,
        stream: true,
        auto_save_history: true,
        additional_messages: [
          {
            role: "user",
            content: message,
            content_type: "text"
          }
        ],
        custom_variables: customVariables
      };

      console.log('🚀 HTTP 回退调用:', JSON.stringify(requestBody, null, 2));

      const response = await fetch('https://api.coze.cn/v3/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP API 响应错误:', response.status, errorText);
        throw new Error(`API请求失败: ${response.status}`);
      }

      // 处理流式响应
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('响应体为空');
      }

      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('🏁 HTTP 流式响应读取完成');
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const content = this.processStreamLine(line);
            if (content) {
              fullContent += content;
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // 处理剩余的buffer
      if (buffer.trim()) {
        const content = this.processStreamLine(buffer);
        if (content) {
          fullContent += content;
        }
      }

      const cleanedContent = this.cleanResponseContent(fullContent);

      if (cleanedContent) {
        console.log('🎯 HTTP 最终提取内容:', cleanedContent);
        return {
          message: cleanedContent,
          success: true
        };
      }

      return {
        message: '智能体已响应，但内容提取失败',
        success: true
      };

    } catch (error) {
      console.error('💥 HTTP API 调用异常:', error);
      return {
        message: '',
        success: false,
        error: error instanceof Error ? error.message : '网络请求失败'
      };
    }
  }

  // 处理流式响应的单行数据
  private processStreamLine(line: string): string {
    const trimmed = line.trim();
    if (!trimmed || !trimmed.startsWith('data: ')) return '';

    const dataContent = trimmed.slice(6).trim();
    if (dataContent === '[DONE]') return '';

    try {
      const data = JSON.parse(dataContent);
      console.log('📝 处理流式数据:', data);

      // 根据事件类型提取内容
      if (data.event) {
        switch (data.event) {
          case 'conversation.message.delta':
            if (data.delta && data.delta.content) {
              return data.delta.content;
            }
            break;
          case 'conversation.message.completed':
            if (data.message && data.message.content) {
              return data.message.content;
            }
            break;
          case 'conversation.chat.completed':
            if (data.messages && Array.isArray(data.messages)) {
              const assistantMsg = data.messages.find(
                (msg: any) => msg.role === 'assistant' && msg.type === 'answer'
              );
              if (assistantMsg && assistantMsg.content) {
                return assistantMsg.content;
              }
            }
            break;
        }
      }

      // 通用内容提取
      return this.extractContentFromStreamData(data);
    } catch (error) {
      console.log('⚠️ 无法解析流式数据:', dataContent);
      return '';
    }
  }

  // 从流式数据中提取内容
  private extractContentFromStreamData(data: any): string {
    if (!data) return '';

    // 检查各种可能的内容字段
    const contentFields = [
      'content',
      'message.content',
      'delta.content',
      'data.content'
    ];

    for (const field of contentFields) {
      const value = this.getNestedValue(data, field);
      if (value && typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    return '';
  }

  // 获取嵌套对象的值
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  }

  // 统一的内容清理方法
  private cleanResponseContent(content: string): string {
    if (!content || typeof content !== 'string') {
      return '';
    }

    let cleaned = content.trim();
    console.log('🧹 开始清理内容:', cleaned.substring(0, 100) + '...');

    // 移除系统标识符和元数据
    const systemPatterns = [
      /ts-keyword_memory-setKeywordMemory/gi,
      /plugin_id/gi,
      /msg_type/gi,
      /conversation_id/gi,
      /bot_id/gi,
      /user_id/gi,
      /\b(id|type|role|timestamp|status|success|error)\b/gi,
      /\b\w*_\w*\b/g, // 下划线分隔的系统字段
      /[{}[\]":,]/g, // JSON 结构字符
      /\b(true|false|null|undefined)\b/gi,
      /\b\d{10,}\b/g, // 长数字（可能是ID或时间戳）
    ];

    for (const pattern of systemPatterns) {
      cleaned = cleaned.replace(pattern, ' ');
    }

    // 清理多余的空白字符
    cleaned = cleaned.replace(/\s+/g, ' ').trim();

    // 提取有意义的中文或英文内容
    const meaningfulContent = this.extractMeaningfulText(cleaned);
    
    if (meaningfulContent) {
      console.log('🎯 提取到有意义内容:', meaningfulContent);
      return meaningfulContent;
    }

    // 如果没有提取到特定模式，返回清理后的内容（如果足够长）
    if (cleaned.length >= 5 && /[a-zA-Z\u4e00-\u9fa5]/.test(cleaned)) {
      console.log('🎯 返回通用清理内容:', cleaned);
      return cleaned;
    }

    console.log('❌ 内容清理失败');
    return '';
  }

  // 提取有意义的文本内容
  private extractMeaningfulText(text: string): string {
    if (!text) return '';

    // 匹配完整的句子或短语
    const patterns = [
      // 欢迎语模式
      /欢迎[^。！？]*?访问[^。！？]*/g,
      /您好[^。！？]*?[。！？]/g,
      /欢迎.*?访问/g,
      // 完整的中文句子
      /[\u4e00-\u9fa5][^。！？]*[。！？]/g,
      // 长的中文短语
      /[\u4e00-\u9fa5]{8,}/g,
      // 英文句子
      /[A-Z][a-zA-Z\s,]{10,}[.!?]/g,
    ];

    for (const pattern of patterns) {
      const matches = text.match(pattern);
      if (matches && matches.length > 0) {
        // 选择最完整的匹配
        const bestMatch = matches.reduce((best, current) => {
          // 优先选择包含关键信息的匹配
          if (current.includes('冯云信息科技') || current.includes('冯鹏程')) {
            return current;
          }
          return current.length > best.length ? current : best;
        });

        if (bestMatch && bestMatch.length >= 5) {
          return bestMatch.trim();
        }
      }
    }

    return '';
  }

  // ... 保留原有的其他方法 ...

  async getConversationHistory(conversationId: string): Promise<CozeMessage[]> {
    if (!this.apiKey) {
      return [];
    }

    try {
      // 优先使用SDK获取对话历史
      if (this.client && window.CozeWebSDK) {
        const response = await this.client.conversations.messages.list({
          conversation_id: conversationId
        });
        
        if (response && response.data) {
          return response.data.map((msg: any) => ({
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at).getTime()
          }));
        }
      }

      // 回退到HTTP接口
      const response = await fetch(`https://api.coze.cn/v1/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
        }
      });

      if (!response.ok) {
        throw new Error(`获取对话历史失败: ${response.status}`);
      }

      const data = await response.json();
      return data.messages || [];
    } catch (error) {
      console.error('获取对话历史失败:', error);
      return [];
    }
  }
}

export const cozeApiService = new CozeApiService();
export type { CozeMessage, CozeResponse, CustomVariables };
